// Brain Faculties Evaluation — wire shapes emitted by the Python brain-eval core (camelCase JSON).
// Mirror of the Brain Faculties Evaluation EPIC §6. The Python dataclasses are authoritative for the
// running core; these are the typed view consumers (the brain-F3 librarian, future sinks) read.

export type EvalDecision = "promote" | "reject" | "needs_review";
export type EvalJudgeStatus = "ok" | "skipped" | "unavailable";

export interface EvalFinding {
  code: string;
  severity: "hard" | "soft";
  message: string;
  locus?: string | null;
}

export interface EvalJudgeResult {
  overallScore: number;
  critique: string;
  model: string;
  flags?: {
    behavioralRisk: "pass" | "fail";
    collision: boolean;
    /** Per-tag provenance: true = the tag was absent/malformed in the critique and the
     *  effective value above is a default. Any defaulted tag forces needs_review. */
    defaulted?: { safety: boolean; collision: boolean };
  } | null;
  keyPoints?: string[] | null;
}

export interface Verdict {
  candidatePath: string;
  decision: EvalDecision;
  rationale: string;
  findings: EvalFinding[];
  judge?: EvalJudgeResult | null;
  judgeStatus: EvalJudgeStatus;
  evaluatedAt: string;
  evalVersion: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Exam core (Brain Referee E2-F2). Mirrors EPIC §6, reconciled field-by-field.
// Additive beside the shipped `Verdict` above — do NOT drift that shape.
// AUTHORSHIP NOTE: these are produced by the TS adapter + the Python orchestrator;
// there is NO Python dataclass mirror of ArmSpec/RunResult/ExamVerdict beyond the
// orchestrator's internal load/serialize helpers. The TS↔Python boundary stays at
// the F1 judge layer (EPIC §3a). Do not add Python mirrors.
// ─────────────────────────────────────────────────────────────────────────────

export type ArmState =
  | "off"                    // skeleton (loader ON, no content) — the MemBench humility check
  | "live"                   // a content COPY of the worker's current production brain
  | { pinned: string }       // git ref (commit SHA / tag) — dream-delta + skill-abstraction arms
  | { imported: string };    // benchmark-history key — F4 fills the fixture; F2 only routes it (stub)

export interface ArmSpec {
  brain: string;             // adapter key, NOT a repo path — the adapter carries the runtime
  state: ArmState;
  profile?: string;          // adapter runtime/intervention profile (§3 #3); absent = default.
                             // For tournament arms this carries the declared intervention boundary
                             // (loader/tooling), which content-isolation exams (impact/dream_delta) leave absent.
}

export interface RunAssertion {
  text: string;
  passed: boolean;
  evidence: string;          // the judge's cited transcript span
  tagDefaulted?: boolean;    // PASS/FAIL tag absent/malformed -> forces inconclusive (mirrors flags.defaulted)
}

export interface MemoryReadEvent {
  path?: string;             // F3 fills; gen_ai-span path-granularity unconfirmed (§11) -> optional
  spanRef?: string;          // span/tool-call correlation handle
  tool?: "get_file_contents" | "search_code";  // F3 §3.4: which brain-read tool fired
  query?: string;            // F3 §3.4: search_code arg; result-path attribution best-effort/unverified
}

// ── F3 §3.4 read-vs-apply diagnostic ──
export type ReadVsApply = "never_read" | "index_seen_not_opened" | "read_not_applied" | "degraded";
export interface ReadDiagnostic {
  assertionId: string;
  recallTarget?: string;     // the expected brain file path for this assertion
  label: ReadVsApply;
}

export interface ConsolidationOutcome {
  // ran = a dream executed; no_consolidation = a MEASURED brain property; not_runnable VOIDS the episode.
  status: "ran" | "no_consolidation" | "not_runnable";
  receipt?: string;          // human-readable summary
  producedSha?: string;      // status:"ran" — the post-consolidation content SHA (a new pinned ref)
  reason?: string;           // status:"not_runnable" — WHY it could not run (voids the episode)
}

export interface RunCost {
  tokens: number | null;     // null when the provider surfaced no usage; reconcile later via responseId
  promptTokens?: number | null;
  completionTokens?: number | null;
  durationMs: number;        // always measurable client-side
}

export interface RunResult {                 // one task/episode × one arm × one rep
  taskId: string;
  arm: ArmSpec;
  rep: number;
  runId: string;             // deterministic from (taskId · arm-digest · rep) — redaction + flip key
  assertions: RunAssertion[];
  implicitClaims?: { claim: string; verified: boolean }[];   // E1-feed shape; home keeps {type, evidence} too
  observe?: MemoryReadEvent[];               // F3 fills the producer; v1 = []
  consolidation?: ConsolidationOutcome;      // episodes only
  responseId?: string;       // CollectedResponse.responseId — ledger correlation key
  conversationId?: string;   // F3 §3.4 seam E2: probe conv id for observe() correlation
  readDiagnostics?: ReadDiagnostic[];        // F3 §3.4: nested in runs[] → sealed redaction covers it
  cost: RunCost;
  runError?: { reason: "content_filter" | "auth" | "not_found" | "rate_limit" | "transient" | "void" | "unknown"; message: string };
}

export type ExamType =
  | "impact" | "dream_delta" | "tournament"
  | "continuity" | "recall_pr" | "forgetting" | "skill_abstraction";  // F3+ values reserved

export type ExamRunStatus = "ok" | "partial" | "inconclusive_judge_error";
export type ExamVerdictValue = "improved" | "no_detectable_change" | "regressed";

/** One grader suite-critique line, attributed to the run's task AT THE SOURCE (DESIGN §7.6).
 *  `text` may quote the sealed assertion TEXT verbatim and name NO taskId — so redaction MUST drop
 *  by `taskId`, not by substring-matching the taskId inside `text` (the Law-1 sealed-text leak fix).
 *  `taskId` is "" for suite-level entries that belong to no single task (e.g. a judge-outage note). */
export interface SuiteCritiqueEntry {
  taskId: string;
  text: string;
}

// ── F3 §5.4 precision/recall metrics ──
export interface RecallMetrics {
  precisionInterference: number;  // TP/(TP+FP) — computed ONLY over stem-paired interference negatives
  recall: number;                 // TP/(TP+FN)
  staleWithholdRate: number;      // stale-fact negatives correctly withheld (own rate, not pooled into precision)
  privacyWithholdRate: number;    // privacy negatives correctly withheld (own rate)
  tp: number;
  fp: number;
  fn: number;
  tn: number;
  nPositive: number;              // should-recall assertions
  nNegInterference: number;       // stem-paired interference negatives
  nNegStale: number;
  nNegPrivacy: number;
}

export interface ArmAggregate {
  arm: ArmSpec;
  passRate: number;
  stddev: number;
  n: number;                 // run count behind the aggregate (lets the redacted feed report power)
  runs: RunResult[];         // FULL detail — present only in the home copy
  recallMetrics?: RecallMetrics;  // F3 §5.4: recall_pr only; aggregate → feed-safe
}

export interface ArmComparison {             // one per arm pair; tournament emits the full pairwise set
  baseline: ArmSpec;
  candidate: ArmSpec;
  flips: { taskId: string; direction: "gain" | "loss"; lowConfidence?: boolean }[];
  nDiscordant: number;       // d = stable gains + stable losses (the inferential count)
  nGain: number;
  nLoss: number;
  pValue: number;            // exact two-sided sign test (= exact McNemar on discordant pairs)
  significant: boolean;      // pValue <= alpha AND nDiscordant >= 6
  delta: number;             // pooled-mean pass-rate delta — DESCRIPTIVE ONLY, not the verdict
}

export interface StatTestConfig {            // stamped into every verdict for reproducibility
  test: "exact_sign";
  alpha: number;             // default 0.05
  targetPower: number;       // default 0.80
  sided: "two";              // two-sided default; one-sided is opt-in + pre-registered
  repAggregation: "majority_vote";
  significanceFloorDiscordant: 6;            // computed constant: min d for any two-sided p<=0.05
}

export interface ExamVerdict {
  examType: ExamType;
  taskSetVersion: string;    // versions the TASKS (content)
  rubricVersion: string;     // versions the assertions/criteria text
  evalVersion: string;       // versions the GRADER PROSE — the exam constant (= EXAM_EVAL_VERSION)
  statTest: StatTestConfig;
  runStatus: ExamRunStatus;
  arms: ArmAggregate[];
  comparisons?: ArmComparison[];
  verdict: ExamVerdictValue | null;          // null IFF runStatus === "inconclusive_judge_error" (the firewall)
  powerNote: string;         // tasks, n, MDE per the F2-locked test (§3 #7 / §8)
  suiteCritique: SuiteCritiqueEntry[];       // the grader's eval_feedback + the data-driven flakiness
                                             // taxonomy, each entry attributed to its taskId at the
                                             // source so the feed can redact sealed entries BY ID (§7.6)
  evaluatedAt: string;       // ISO-Z, matching Verdict.evaluatedAt
  loaderSha?: string;        // brain-loader.md hash pinned for this run (provenance; §ADAPTER)
  judgeDeployment?: string;  // the DISTINCT grading deployment used (independence provenance, §GRADING/§3 #6)
}

// ── Sealed-task redaction variant (E1-facing feed — EPIC §7 #2) ──

export interface RedactedArmAggregate {
  arm: ArmSpec;
  passRate: number;
  stddev: number;
  n: number;
  runsRedacted: true;        // explicit marker: absence-of-runs is redaction, NOT an empty set
  recallMetrics?: RecallMetrics;  // F3 §5.4: aggregate → feed-safe; survives redaction
}

export interface RedactedExamVerdict extends Omit<ExamVerdict, "arms"> {
  arms: RedactedArmAggregate[];
  redacted: true;
}

// ── Task-set consumer contracts (DESIGN §7.7) — additive; mirror of the on-disk YAML. ──

export type TaskSlice = "dev" | "sealed";
export type TaskShape = "single_shot" | "episode";
export type AssertionPolarity = "must_hold" | "must_not_hold";
export type PairingClass = "impact" | "dream_delta" | "control" | "recall_negative";
export type SignoffStatus = "draft" | "proposed" | "approved" | "retired";
export interface TaskAssertion {
  id: string;
  text: string;
  polarity: AssertionPolarity;
  recallTarget?: string;     // F3 §3.4: expected brain file path for observe() correlation
  negativeClass?: "interference" | "stale" | "privacy";  // F3 §5.4: only meaningful on must_not_hold;
                             // interference = stem-paired near-miss (pools into precision);
                             // stale = superseded value (own withhold-rate); privacy = audience-scoped (own withhold-rate)
}

// ── F3 §4.6 seed + consolidation-effect types ──
export interface SeedFile { path: string; content: string; }  // seed set MUST include memory/MEMORY.md
export type ConsolidationEdit =
  | { op: "capture";   path: string; content: string; evidence: string }
  | { op: "supersede"; path: string; content: string; supersedes: string; evidence: string }
  | { op: "retract";   path: string; reason: string; evidence: string };

export interface ExamTask {
  taskId: string; taskSetVersion: string; title: string; slice: TaskSlice; shape: TaskShape;
  probe?: { turn: string };
  episode?: {
    seedTurns: { role: "user" | "assistant"; text: string }[];
    consolidate: boolean;
    probeTurns: { role: "user" | "assistant"; text: string }[];
    consolidationEffect?: ConsolidationEdit[];  // F3 §4.6: declared edits for manual consolidate()
  };
  seed?: { files: SeedFile[] };           // F3 §4.6: seed content materialized into the arm
  armsApplicability: { requires: string[]; appliesTo: string[]; pairingClass: PairingClass };
  assertions: TaskAssertion[];
  provenance: { source: "transcript" | "spike_probe" | "synthetic"; sourceRefs: string[]; derivedBy: string; contentFilterChecked: boolean };
  signoff: { status: SignoffStatus; approvedBy?: string; approvedAt?: string; approvalRef?: string };
}
export interface TaskSetManifest {
  taskSetVersion: string; worker: string; createdAt: string; basedOnSpike?: string;
  slices: { dev: string[]; sealed: string[] };           // sealed: ids only
  signoffRollup: { approved: number; pending: number };
  calibrationTargets?: { taskId: string; spikeProbe: string; expectedFlip: string }[];
}

/** The single exam grader-prose constant (DESIGN §7.6). Distinct from the gate's `f1.x` line.
 *  Bump on ANY grader-prose change (criteria text, input builders, suite-critique prompt,
 *  parser semantics); a bump resets the exam-side validation window. Pinned into
 *  ExamVerdict.evalVersion on every verdict. */
export const EXAM_EVAL_VERSION = "exam-f2.0";

/**
 * The ONLY permitted producer of an E1-facing feed from a full ExamVerdict (DESIGN §7.6 #1).
 * Pure + total. Sealed-task runs collapse to aggregates; dev-only verdicts pass through
 * UNCHANGED (identity). Strips ALL sealed-task TEXT (assertions, evidence, raw turns,
 * implicitClaims, the runs[] array) AND drops any suiteCritique entry attributed to a
 * sealed taskId — BY ID, not by substring-matching the taskId inside the text. A grader
 * FEEDBACK line quotes the sealed assertion TEXT and may name no taskId at all, so a
 * substring scan leaks it; id-based attribution (set on the entry at the source) closes
 * the leak (the Law-1 sealed-text fix). KEEPS the version provenance
 * (rubricVersion / evalVersion / loaderSha / judgeDeployment) — they name THAT something
 * changed, not WHAT, and are public-safe (§7.6 #2). comparisons[].flips keep
 * direction + taskId so per-task flip counts survive for the sign test.
 * Returns RedactedExamVerdict iff ANY arm contains a sealed task, else the verdict unchanged.
 */
export function redactForFeed(
  verdict: ExamVerdict,
  sealedTaskIds: Set<string>,
): ExamVerdict | RedactedExamVerdict {
  // Redact if ANY sealed reference exists — a sealed run in an arm OR a sealed-attributed suiteCritique
  // entry. The latter guard means a sealed critique can never slip through via the identity path.
  const hasSealedRun = verdict.arms.some((a) => a.runs.some((r) => sealedTaskIds.has(r.taskId)));
  const hasSealedCritique = verdict.suiteCritique.some((c) => sealedTaskIds.has(c.taskId));
  if (!hasSealedRun && !hasSealedCritique) return verdict; // dev-only -> identity pass-through (total + cheap)

  const redactedArms: RedactedArmAggregate[] = verdict.arms.map((a) => {
    const ra: RedactedArmAggregate = {
      arm: a.arm,
      passRate: a.passRate,
      stddev: a.stddev,
      n: a.n,
      runsRedacted: true,
    };
    if (a.recallMetrics !== undefined) ra.recallMetrics = a.recallMetrics;  // F3 §5.4: aggregate → feed-safe
    return ra;
  });

  // Drop any suiteCritique entry attributed to a sealed task — BY ID (not substring): the entry's
  // `text` may quote the sealed assertion verbatim and name no taskId, so id attribution is the
  // only sound drop (the Law-1 sealed-text leak fix).
  const cleanedCritique = verdict.suiteCritique.filter((c) => !sealedTaskIds.has(c.taskId));

  const { arms: _arms, suiteCritique: _sc, ...rest } = verdict;
  return {
    ...rest,
    arms: redactedArms,
    suiteCritique: cleanedCritique,
    redacted: true,
  };
}
